import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import PolicyholderFnol from "@icg360/fnol-policyholder";

import { FullpageSpinner } from "components/shared/fullpage-spinner";
import { CONFIG } from "config";
import {
  useClaimsConstants,
  usePolicyDetail,
  usePolicyPlatformId,
  useUser,
} from "data/ss-data-hooks";
import { useKeystone } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { type PolicyHolderDetails } from "gql/__generated__/hooks";
import { PageError } from "pages/page-error";
import { trackEvent, trackFNOLEvent } from "utils";

const ExternalClaimSubmit = () => {
  const keystone = useKeystone();
  const navigate = useNavigate();
  const selectedPolicyId = usePolicyPlatformId();
  const user = useUser();
  const [pageError, setPageError] = useState<boolean>(false);
  const { data: policyDetail, error, isLoading } = usePolicyDetail();
  const { isLoading: isConstantsLoading, data: claimsConstants } =
    useClaimsConstants();
  const [policyHolderDetails, setPolicyHolderDetails] =
    useState<PolicyHolderDetails>();

  useEffect(() => {
    trackEvent("Claim Submit Form: Displayed");
  }, []);

  useEffect(() => {
    if (error) setPageError(true);

    if (!isConstantsLoading && claimsConstants) {
      setPolicyHolderDetails(claimsConstants.PolicyHolderDetails);
    }
  }, [
    error,
    claimsConstants?.PolicyHolderDetails,
    isConstantsLoading,
    claimsConstants,
  ]);

  if (isLoading) {
    return <FullpageSpinner />;
  }

  if (pageError || !selectedPolicyId) {
    return <PageError />;
  }

  const propertyAddress = policyDetail?.userDetails.propertyAddress;
  const currentTerm = policyDetail?.userDetails.currentTerm;

  const policy = {
    policyId: selectedPolicyId,
    policyName: `${policyDetail?.userPolicyProperties?.carrierName} (${policyDetail?.userPolicyProperties?.policyType})`,
    policyEffectiveDate: currentTerm?.effectiveDate,
    policyExpirationDate: currentTerm?.expirationDate,
    fullName: policyHolderDetails?.name ?? "",
    email: policyHolderDetails?.emailAddress ?? "",
    phoneNumber: policyHolderDetails?.phoneNumber ?? "",
    streetAddress: `${propertyAddress?.street1} ${propertyAddress?.street2}`,
    city: propertyAddress?.city ?? "",
    state: propertyAddress?.state ?? "",
    zip: propertyAddress?.zip ?? "",
    carrierGroup: policyDetail?.userPolicyProperties?.carrierGroup ?? "",
    policyType: policyDetail?.userPolicyProperties?.policyType ?? "",
  };

  const handleFNOLPostApi = async (event) => {
    const response = await keystone.fnolChannelSubmit(event);
    if (isError(response)) {
      setPageError(true);
    }
    return response;
  };

  const handleFormExit = () => {
    navigate("/my/claims");
  };

  return (
    <PolicyholderFnol
      definitionLocation={CONFIG.FNOL_DEFINITIONS_URL}
      policy={policy}
      user={user?.email ?? undefined}
      googleMapsKey={CONFIG.GOOGLE_MAPS_KEY}
      onPost={handleFNOLPostApi}
      onExit={handleFormExit}
      onEvent={(event) => {
        trackFNOLEvent(event);
      }}
    />
  );
};

export default ExternalClaimSubmit;
